import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { AlumnusCard } from "../components/AlumnusCard";
import { DEPARTMENT_SHORT, DISTINGUISHED, groupAlumniByDepartment } from "../lib/alumni";
import {
  DEAN_ALUMNI,
  DEAN_ALUMNI_EMAIL,
  DEPARTMENT_COORDINATORS,
  EXECUTIVE_COMMITTEE,
  HISTORY,
  MISSION,
  OBJECTIVES,
  VISION,
} from "../lib/alumniGovernance";
import { ALUMNI_EVENTS, ALUMNI_GUEST_HOUSE, ALUMNI_REGISTRATION_URL } from "../lib/alumniEvents";
import { ALUMNI_MEET, daysUntilMeet } from "../lib/alumniMeet";
import { ROUTES } from "../lib/routes";

const ALL = "All";

/**
 * Everyone holding office, the Dean first and then the five elected bearers in
 * the order the Association lists them. One roster rather than two blocks: the
 * Dean alone in his own row left most of that row empty. Six over three columns
 * is two full rows. `appointed` keeps the distinction the section draws — the
 * Dean is appointed by the institute, the rest are elected.
 */
const OFFICE_BEARERS = [
  { role: "Dean · Alumni Relations", name: DEAN_ALUMNI.name, title: "", email: DEAN_ALUMNI_EMAIL, appointed: true },
  { role: "President", ...EXECUTIVE_COMMITTEE.president },
  { role: "Vice President", ...EXECUTIVE_COMMITTEE.vicePresident },
  { role: "Secretary", ...EXECUTIVE_COMMITTEE.secretary },
  { role: "Joint Secretary", ...EXECUTIVE_COMMITTEE.jointSecretary },
  { role: "Treasurer", ...EXECUTIVE_COMMITTEE.treasurer },
] as { role: string; name: string; title: string; email?: string; appointed?: boolean }[];

export default function Alumni() {
  // Computed after mount: a prerendered page would otherwise ship the count
  // from build day.
  const [daysToMeet, setDaysToMeet] = useState<number | null>(null);
  useEffect(() => setDaysToMeet(daysUntilMeet()), []);
  const groups = groupAlumniByDepartment();
  // ?dept=<full department name> preselects a tab, so a department page can
  // link straight to its own slice of the roster. An unknown value falls back
  // to All rather than showing an empty panel.
  const [params] = useSearchParams();
  const fromUrl = params.get("dept");
  const initial = groups.some((g) => g.department === fromUrl) ? fromUrl! : ALL;
  const [activeDept, setActiveDept] = useState<string>(initial);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // The Alumni page stays mounted across a ?dept= change, so follow the URL.
  useEffect(() => setActiveDept(initial), [initial]);

  const tabs = [
    { key: ALL, label: "All", count: DISTINGUISHED.length },
    ...groups.map((g) => ({
      key: g.department,
      label: DEPARTMENT_SHORT[g.department] ?? g.department,
      count: g.alumni.length,
    })),
  ];

  const visible =
    activeDept === ALL
      ? DISTINGUISHED
      : groups.find((g) => g.department === activeDept)?.alumni ?? [];

  // Roving-focus keyboard nav across the tab strip (WAI-ARIA tabs pattern).
  function onTabKeyDown(e: React.KeyboardEvent, i: number) {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!delta && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : (i + delta + tabs.length) % tabs.length;
    setActiveDept(tabs[next].key);
    tabRefs.current[next]?.focus();
  }

  return (
    <>
      <section className="section al-meet-wrap" id="global-meet" aria-labelledby="al-meet-title">
        <div className="container">
          <header className="al-meet-head">
            <div className="kicker">Flagship event · {ALUMNI_MEET.dateLabel}</div>
            <h2 id="al-meet-title">
              {ALUMNI_MEET.title}
              <span lang="kn">{ALUMNI_MEET.kannada}</span>
            </h2>
            <p className="al-meet-tag">{ALUMNI_MEET.tagline}</p>
          </header>
          <div className="al-meet">
            <a className="al-meet-poster" href={ALUMNI_MEET.poster.url} target="_blank" rel="noreferrer">
              <img
                src={ALUMNI_MEET.poster.url}
                alt={ALUMNI_MEET.poster.alt}
                width={ALUMNI_MEET.poster.width}
                height={ALUMNI_MEET.poster.height}
                loading="lazy"
              />
            </a>
            <div className="al-meet-body">
              <p className="al-meet-lead">{ALUMNI_MEET.blurb}</p>
              <dl className="al-meet-facts">
                <div>
                  <dt>Date</dt>
                  <dd>{ALUMNI_MEET.weekday}, {ALUMNI_MEET.dateLabel}</dd>
                </div>
                <div>
                  <dt>Time</dt>
                  <dd>{ALUMNI_MEET.timeLabel}</dd>
                </div>
                <div>
                  <dt>Venue</dt>
                  <dd>{ALUMNI_MEET.venue}, {ALUMNI_MEET.place}</dd>
                </div>
              </dl>
              {daysToMeet !== null && (
                <p className="al-meet-count">
                  <b>{daysToMeet === 0 ? "Today" : daysToMeet}</b>
                  {daysToMeet === 0 ? "" : daysToMeet === 1 ? " day to go" : " days to go"}
                </p>
              )}
              <p className="al-meet-reg">Scan the QR code on the poster to register.</p>
            </div>
          </div>
          <ul className="al-meet-pillars">
            {ALUMNI_MEET.pillars.map((p) => (
              <li key={p.head}>
                <b>{p.head}</b>
                <span>{p.text}</span>
              </li>
            ))}
          </ul>
          <p className="al-meet-motto">{ALUMNI_MEET.motto}</p>
        </div>
      </section>

      <PageHero
        variant="hero-editorial"
        eyebrow="Alumni Association · Est. 1994"
        title="Bridging BIET and the engineers it sent into the world."
        lead="Established in 1994, the BIET Alumni Association connects generations of graduates with their alma mater, faculty, and students. Through department coordinators, alumni meets, mentoring, and professional engagement, it strengthens lifelong relationships and creates opportunities for knowledge sharing, industry interaction, and student development."
        crumbs={[{ label: "Connect" }, { label: "Alumni" }]}
        heroMedia={ALUMNI_GUEST_HOUSE.hero}
        image="ALUMNI GUEST HOUSE"
      />

      <section className="section">
        <div className="container">
          {/* ─── Charter: vision · mission · objectives ───── */}
          <div className="charter">
            <figure className="charter-vision">
              <div className="kicker">Our vision</div>
              <blockquote>{VISION}</blockquote>
            </figure>

            <div className="charter-pillars">
              <div className="charter-group">
                <h3 className="charter-head">Mission</h3>
                <ol className="charter-list">
                  {MISSION.map((m, i) => (
                    <li key={i}>
                      <span className="n">{String(i + 1).padStart(2, "0")}</span>
                      <span className="t">{m}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="charter-group">
                <h3 className="charter-head">Objectives</h3>
                <ol className="charter-list">
                  {OBJECTIVES.map((o, i) => (
                    <li key={i}>
                      <span className="n">{String(i + 1).padStart(2, "0")}</span>
                      <span className="t">{o}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>

          {/* ─── History ─────────────────────────────────── */}
          <div className="al-section-head">
            <h2>A short history</h2>
            <p>Milestones since the Association was founded in {HISTORY.established}.</p>
          </div>
          <ol className="al-timeline">
            {HISTORY.milestones.map((m, i) => (
              <li key={i} className="reveal">
                <span className="yr">{m.year}</span>
                <span className="tx">{m.what}</span>
              </li>
            ))}
          </ol>

          {/* ─── Leadership ──────────────────────────────── */}
          <div className="al-section-head">
            <h2>Governance and office bearers</h2>
            <p>The Dean for alumni relations, and the Association&rsquo;s elected office bearers.</p>
          </div>

          <div className="al-office">
            {OFFICE_BEARERS.map((o, i) => (
              <div key={i} className={`al-office-card${o.appointed ? " is-appointed" : ""}`}>
                <div className="al-office-role">{o.role}</div>
                <div className="al-office-name">{o.name}</div>
                {o.title && <div className="al-office-title">{o.title}</div>}
                {o.email && (
                  <a className="al-office-mail" href={`mailto:${o.email}`}>{o.email}</a>
                )}
              </div>
            ))}
          </div>

          <div className="al-members">
            <div className="al-members-head">Directors</div>
            <ul>
              {EXECUTIVE_COMMITTEE.directors.map((m, i) => (
                <li key={i}>
                  <span className="nm">{m.name}</span>
                  {m.title && <span className="ti">{m.title}</span>}
                </li>
              ))}
            </ul>
          </div>

          {/* ─── Coordinators (table) ────────────────────── */}
          <div className="al-section-head">
            <h2>Department alumni coordinators</h2>
            <p>Every department has a designated coordinator who runs its meets and keeps its alumni register current. Reach yours directly.</p>
          </div>
          <table className="al-coordinators">
            <thead>
              <tr>
                <th>Department</th>
                <th>Coordinator</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {DEPARTMENT_COORDINATORS.map((c, i) => (
                <tr key={i}>
                  <td data-label="Department" className="dn">{c.department}</td>
                  <td data-label="Coordinator" className="cn">{c.name}</td>
                  <td data-label="Email">
                    {c.email ? (
                      <a href={`mailto:${c.email}`}>{c.email}</a>
                    ) : (
                      <span className="na">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ─── Distinguished alumni (tabbed) ───────────── */}
          <div className="al-section-head">
            <h2>Distinguished alumni</h2>
            <p>Graduates across ten departments who have gone on to notable work in industry, public service and research. Filter by department.</p>
          </div>

          <div className="filter-tabs" role="tablist" aria-label="Filter distinguished alumni by department">
            {tabs.map((t, i) => (
              <button
                key={t.key}
                ref={(el) => { tabRefs.current[i] = el; }}
                role="tab"
                type="button"
                id={`al-tab-${i}`}
                aria-selected={activeDept === t.key}
                aria-controls="al-dist-panel"
                tabIndex={activeDept === t.key ? 0 : -1}
                className={"filter-tab" + (activeDept === t.key ? " is-active" : "")}
                onClick={() => setActiveDept(t.key)}
                onKeyDown={(e) => onTabKeyDown(e, i)}
              >
                {t.label}
                <span className="filter-tab-count">{t.count}</span>
              </button>
            ))}
          </div>

          <div
            id="al-dist-panel"
            role="tabpanel"
            aria-labelledby={`al-tab-${tabs.findIndex((t) => t.key === activeDept)}`}
            className="dist-grid al-dist-grid"
          >
            {visible.map((a) => (
              <AlumnusCard key={a.slug} a={a} showDept={activeDept === ALL} />
            ))}
          </div>

          {/* ─── Recent meets ────────────────────────────── */}
          <div className="al-section-head">
            <h2>Recent alumni meets</h2>
            <p>Department-hosted gatherings from the Association's most recent cycle.</p>
          </div>
          <ul className="al-events">
            {ALUMNI_EVENTS.map((e, i) => (
              <li key={i}>
                <span className="dt">{e.date}</span>
                <span className="ti">{e.title}</span>
                <span className="lo">
                  {e.location}
                  {e.time && <em>{e.time}</em>}
                </span>
              </li>
            ))}
          </ul>

          {/* ─── Guest house ─────────────────────────────── */}
          <div className="al-section-head">
            <h2>Alumni Guest House</h2>
            <p>{ALUMNI_GUEST_HOUSE.intro}</p>
          </div>
          <div className="al-gallery">
            {ALUMNI_GUEST_HOUSE.photos.map((p, i) => (
              <figure key={i} className="reveal">
                <Figure media={p} fallbackLabel={p.alt ?? "ALUMNI GUEST HOUSE"} aspect="3/2" sizes="(max-width: 760px) 100vw, 45vw" />
                <figcaption>{p.alt}</figcaption>
              </figure>
            ))}
          </div>

          {/* ─── Register + contact ──────────────────────── */}
          <div className="al-cta reveal">
            <div>
              <div className="kicker">Stay on the register</div>
              <h2>Register with the Alumni Association.</h2>
              <p>
                The Association maintains a central alumni database, open to every graduate regardless
                of branch or year. Registering keeps you in the loop for department meets and Alumni
                Cell communications.
              </p>
              <a className="btn btn-primary" href={ALUMNI_REGISTRATION_URL} target="_blank" rel="noreferrer">
                Register / update your details →
              </a>
            </div>
            <dl className="al-cta-contact">
              <dt>Dean · Alumni Relations</dt>
              <dd>{DEAN_ALUMNI.name}</dd>
              <dt>Email</dt>
              <dd><a href={`mailto:${DEAN_ALUMNI_EMAIL}`}>{DEAN_ALUMNI_EMAIL}</a></dd>
              <dt>Institute</dt>
              <dd><a href="mailto:principal@bietdvg.edu">principal@bietdvg.edu</a></dd>
              <dt>Phone</dt>
              <dd>+91 8192 222245</dd>
            </dl>
          </div>
        </div>
      </section>
    </>
  );
}
